// Shared ordering for CBQ scenarios and questions.
//
// The server keeps position in `sortKey`, a fractional-index string ("a0",
// "a1", "a0V" …) rewritten on every insert / reorder. Numeric `order` is only
// backfilled when the exam is published, so draft rows all carry `order: 0`
// and can be told apart by `sortKey` alone. Sorting on `order` first therefore
// scrambles a draft and hides every reorder.
//
// Ordering must not depend on the JS engine either. Subtracting raw `order`
// fields yields NaN when the server omits one, and a NaN comparator leaves the
// result implementation-defined — older Android WebViews also sort unstably
// above 10 elements, so the same payload could order differently there than on
// desktop. Missing or non-finite orders sort last, and rows that still tie keep
// the position the server sent them in (see `sortCbq`).

export interface CbqOrdered {
  id?: string;
  sortKey?: string | null;
  order?: number | null;
}

const orderRank = (v: unknown) =>
  typeof v === "number" && Number.isFinite(v) ? v : Number.MAX_SAFE_INTEGER;

// Code-unit comparison, not localeCompare: fractional keys are ASCII and their
// meaning is plain lexicographic. Collation rules would reorder case and digits.
const bySortKey = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

/**
 * Compare two rows by position. Ties (equal `sortKey`, or equal/absent `order`
 * with no `sortKey` to separate them) return 0 — `sortCbq` breaks those on
 * incoming index. Exported for call sites that already sort in place.
 */
export const byCbqOrder = (a: CbqOrdered, b: CbqOrdered): number => {
  if (a.sortKey && b.sortKey) return bySortKey(a.sortKey, b.sortKey);
  return orderRank(a.order) - orderRank(b.order);
};

/**
 * Sort a server list into display order, leaving equal-position rows in the
 * order the server sent them. The index tie-break is explicit rather than
 * relying on `Array.prototype.sort` being stable.
 */
export const sortCbq = <T extends CbqOrdered>(list: readonly T[]): T[] =>
  list
    .map((item, index) => ({ item, index }))
    .sort((a, b) => byCbqOrder(a.item, b.item) || a.index - b.index)
    .map((e) => e.item);
